<!-- src/views/SummarySessionReport.vue -->
<template>
  <div class="container mx-auto px-4 py-8 bg-gray-50">
    <!-- Session Selection -->
    <div class="mb-8">
      <h1 class="text-3xl font-bold mb-6 text-center text-customHafadhColor">تقرير الجلسات</h1>
      <div class="flex justify-center gap-4 items-center flex-wrap">
        <button
          v-if="selectedSessionId && !loading"
          @click="downloadPDF"
          class="px-3 py-2 bg-customHafadhColor text-white rounded-lg hover:bg-customHafadhColor/90 transition-colors duration-300 flex items-center justify-center"
          :disabled="loading"
          title="تحميل PDF"
        >
          <i class="fas fa-download"></i>
        </button>
        <input
          type="date"
          v-model="selectedDate"
          @change="handleSessionChange"
          class="block px-4 py-2 border-2 border-customHafadhColor rounded-lg focus:outline-none focus:ring-2 focus:ring-customHafadhColor"
        />
        <select 
          v-model="selectedSessionId"
          @change="handleSessionChange"
          class="block w-64 px-4 py-2 bg-white border-2 border-customHafadhColor rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-customHafadhColor">
          <option value="">اختر الجلسة</option>
          <option v-for="session in sessions" :key="session.id" :value="session.id">
            {{ session.sessionsName }}
          </option>
        </select>
        
        <select
          v-model="selectedOrganizationId"
          class="block w-64 px-4 py-2 bg-white border-2 border-customHafadhColor rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-customHafadhColor">
          <option value="">اختر الجهة</option>
          <option v-for="org in organizations" :key="org.id" :value="org.id">
            {{ org.name }}
          </option>
        </select>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="text-center py-8">
      <div class="inline-block animate-spin rounded-full h-12 w-12 border-4 border-customHafadhColor border-t-transparent"></div>
      <p class="mt-4 text-lg text-customHafadhColor">جاري التحميل...</p>
    </div>

    <!-- Error State -->
    <div v-if="error" class="bg-red-100 border-2 border-red-400 text-red-700 px-6 py-4 rounded-lg relative text-right mb-4">
      <strong class="font-bold ml-2">خطأ!</strong>
      <span class="block sm:inline">{{ error }}</span>
    </div>

    <!-- Report Content -->
    <div v-if="report && !loading" class="summary-report">
      <!-- PDF Header (hidden on screen, visible in PDF only) -->
      <div class="pdf-header" style="display: none;">
        <div class="header-top">
          <div class="header-logos">
            <div class="logo-left">
              <img :src="appLogo" alt="شعار التطبيق" crossorigin="anonymous" />
            </div>
            <div class="logo-right">
              <img v-if="selectedOrganization && selectedOrganization.logoUrl" 
                   :src="selectedOrganization.logoUrl" 
                   :alt="selectedOrganization.name" 
                   crossorigin="anonymous" 
                   @error="onOrgLogoError" />
              <img v-else-if="selectedOrganization && selectedOrganization.logoBase64" 
                   :src="`data:image/jpeg;base64,${selectedOrganization.logoBase64}`" 
                   :alt="selectedOrganization.name" 
                   crossorigin="anonymous" 
                   @error="onOrgLogoError" />
              <div v-else-if="selectedOrganization" class="logo-placeholder">
                <i class="fas fa-building"></i>
                <span class="placeholder-text">{{ selectedOrganization.name.charAt(0) }}</span>
              </div>
              <div v-else class="logo-placeholder">
                <i class="fas fa-building"></i>
              </div>
            </div>
          </div>
          <div class="header-divider"></div>
        </div>
        <div class="header-content">
          <h2 class="report-title">تقرير الجلسات</h2>
          <div class="report-info">
            <div class="info-row">
              <span class="info-label">التاريخ:</span>
              <span class="info-value">{{ formattedDate }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">الجلسة:</span>
              <span class="info-value">{{ selectedSessionName }}</span>
            </div>
          </div>
        </div>
        <div class="header-separator"></div>
      </div>
      
      <!-- Summary Boxes -->
      <div class="grid grid-cols-4 gap-4 mb-8">
        <div class="summary-box text-center bg-gradient-to-br from-customHafadhColor to-customHafadhColor/80 p-4 shadow-lg transform hover:scale-105 transition-all duration-300">
          <h3 class="text-2xl font-semibold text-white mb-2">المراجعة</h3>
          <p class="text-4xl font-bold text-white">{{ report.totalReview || 0 }}</p>
        </div>
        <div class="summary-box text-center bg-gradient-to-br from-customHafadhColor to-customHafadhColor/80 p-4 shadow-lg transform hover:scale-105 transition-all duration-300">
          <h3 class="text-2xl font-semibold text-white mb-2">الحفظ</h3>
          <p class="text-4xl font-bold text-white">{{ report.totalMemorization || 0 }}</p>
        </div>
        <div class="summary-box text-center bg-gradient-to-br from-customHafadhColor to-customHafadhColor/80 p-4 shadow-lg transform hover:scale-105 transition-all duration-300">
          <h3 class="text-2xl font-semibold text-white mb-2">مجموع الأوجه</h3>
          <p class="text-4xl font-bold text-white">{{ totalPagesSum }}</p>
        </div>
        <div class="summary-box text-center bg-gradient-to-br from-customHafadhColor to-customHafadhColor/80 p-4 shadow-lg transform hover:scale-105 transition-all duration-300">
          <h3 class="text-2xl font-semibold text-white mb-2">مجموع الأجزاء</h3>
          <p class="text-4xl font-bold text-white">{{ totalPartsSum }}</p>
        </div>
      </div>

      <!-- Apologies Section -->
      <div class="apologies-section bg-white p-8 shadow-lg border-2 border-customHafadhColor rounded-lg mb-8 no-break">
        <div class="flex items-center justify-center gap-4 flex-wrap">
          <span class="text-2xl font-semibold text-gray-800">إجمالي الاعتذارات و الغياب:</span>
          <span class="text-4xl font-bold text-customHafadhColor">{{ report.totalApologies || 0 }}</span>
        </div>
      </div>

      <!-- Detailed Student Data Section (if available) -->
      <div v-if="report.studentReports && report.studentReports.length > 0" class="student-details-section bg-white p-6 shadow-lg border-2 border-customHafadhColor rounded-lg mt-8">
        <h3 class="text-3xl font-bold mb-6 text-center text-customHafadhColor bg-gray-50 py-3 rounded-md">تفاصيل الدارسين</h3>
        <div class="table-container">
          <table class="min-w-full divide-y divide-gray-300 border-2 border-customHafadhColor rounded-lg overflow-hidden">
            <thead>
              <tr class="bg-gradient-to-r from-customHafadhColor to-customHafadhColor/90">
                <th class="px-6 py-4 text-right font-bold text-white border-l-2 border-white/20" style="direction: rtl; unicode-bidi: bidi-override; font-size: 1.43rem; font-family: 'Arial Unicode MS', 'Tahoma', 'Arial', sans-serif;">الاسم</th>
                <th class="px-6 py-4 text-center font-bold text-white border-l-2 border-white/20" style="direction: rtl; unicode-bidi: bidi-override; font-size: 1.43rem; font-family: 'Arial Unicode MS', 'Tahoma', 'Arial', sans-serif;">المراجعة</th>
                <th class="px-6 py-4 text-center font-bold text-white border-l-2 border-white/20" style="direction: rtl; unicode-bidi: bidi-override; font-size: 1.43rem; font-family: 'Arial Unicode MS', 'Tahoma', 'Arial', sans-serif;">الحفظ</th>
                <th class="px-6 py-4 text-center font-bold text-white" style="direction: rtl; unicode-bidi: bidi-override; font-size: 1.43rem; font-family: 'Arial Unicode MS', 'Tahoma', 'Arial', sans-serif;">المجموع</th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr v-for="(student, index) in report.studentReports" :key="student.studentId || index" 
                  :class="index % 2 === 0 ? 'bg-white' : 'bg-gray-50'"
                  class="hover:bg-blue-50 transition-colors duration-200">
                <td class="px-6 py-3 text-right text-gray-800 font-medium border-l-2 border-gray-200" 
                    style="direction: rtl; unicode-bidi: bidi-override; font-size: 1.43rem; font-family: 'Arial Unicode MS', 'Tahoma', 'Arial', sans-serif;">
                  {{ student.studentName || student.name || 'غير محدد' }}
                </td>
                <td class="px-6 py-3 text-center text-gray-800 font-semibold border-l-2 border-gray-200">
                  <span class="inline-block bg-blue-100 text-blue-800 px-3 py-2 rounded-full font-bold" style="font-size: 1.3rem;">
                    {{ student.totalReview || student.review || 0 }}
                  </span>
                </td>
                <td class="px-6 py-3 text-center text-gray-800 font-semibold border-l-2 border-gray-200">
                  <span class="inline-block bg-green-100 text-green-800 px-3 py-2 rounded-full font-bold" style="font-size: 1.3rem;">
                    {{ student.totalMemorization || student.memorization || 0 }}
                  </span>
                </td>
                <td class="px-6 py-3 text-center text-gray-800 font-semibold">
                  <span class="inline-block bg-customHafadhColor text-white px-3 py-2 rounded-full font-bold" style="font-size: 1.3rem;">
                    {{ (student.totalReview || student.review || 0) + (student.totalMemorization || student.memorization || 0) }}
                  </span>
                </td>
              </tr>
              <tr v-if="!report.studentReports || report.studentReports.length === 0">
                <td colspan="4" class="px-6 py-8 text-center text-gray-500 italic text-lg">لا يوجد تفاصيل للدارسين متاحة</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      
      <!-- Bottom spacing for PDF -->
      <div class="pdf-bottom-space" style="height: 100px;"></div>
    </div>

    <div v-else-if="!loading && !error" class="text-center py-12 text-gray-500 text-lg">
      الرجاء اختيار جلسة وتاريخ لعرض التقرير.
    </div>
  </div>
</template>

<script>
import url from '@/url';
import axios from 'axios';
import html2pdf from 'html2pdf.js';
import appLogo from '@/assets/logo.png';

export default {
  data() {
    return {
      sessions: [],
      organizations: [],
      selectedSessionId: '',
      selectedOrganizationId: '',
      selectedDate: new Date().toISOString().split('T')[0],
      report: null,
      loading: false,
      error: null,
      appLogo
    };
  },
  computed: {
    selectedSessionName() {
      const session = this.sessions.find(s => s.id == this.selectedSessionId);
      return session ? session.sessionsName : '';
    },
    selectedOrganization() {
      const org = this.organizations.find(org => org.id == this.selectedOrganizationId);
      if (org) {
        console.log('Found organization:', org);
      }
      return org;
    },
    totalPagesSum() {
      return (this.report?.totalMemorization || 0) + (this.report?.totalReview || 0);
    },
    totalPartsSum() {
      return (this.totalPagesSum / 20).toFixed(2);
    },
    formattedDate() {
      if (!this.selectedDate) return '';
      const d = new Date(this.selectedDate);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
  },
  methods: {
    async fetchSessions() {
      try {
        const response = await axios.get(process.env.VUE_APP_SERVICE_URL + url.master_sessions, {
          headers: {
            'X-Authorization': localStorage.getItem('token')
          }
        });
        if (response.data?.result?.data) {
          this.sessions = response.data.result.data;
        }
      } catch (err) {
        this.error = err.response?.data?.mesAr || 'حدث خطأ أثناء تحميل الجلسات';
        console.error('Error fetching sessions:', err);
      }
    },
    async fetchOrganizations() {
      try {
        const response = await axios.get(process.env.VUE_APP_SERVICE_URL + url.organizations, {
          headers: {
            'X-Authorization': localStorage.getItem('token')
          }
        });
        if (response.data?.result?.data) {
          this.organizations = response.data.result.data;
          console.log('Fetched organizations:', this.organizations);
        } else {
          console.log('No organizations data in response:', response.data);
        }
      } catch (err) {
        console.error('Error fetching organizations:', err);
        // Don't show error for organizations as it's optional
      }
    },
    async handleSessionChange() {
      if (!this.selectedSessionId) {
        this.clearReport();
        return;
      }
      await this.fetchReport();
    },
    clearReport() {
      this.report = null;
      this.error = null;
    },
    async fetchReport() {
      this.loading = true;
      this.error = null;
      try {
        const response = await axios.get(
          process.env.VUE_APP_SERVICE_URL + url.summary_session_report + this.selectedSessionId,
          {
            params: { date: this.selectedDate },
            headers: {
              'X-Authorization': localStorage.getItem('token')
            }
          }
        );
        if (response.data?.result?.data) {
          this.report = response.data.result.data;
          console.log('Summary report data:', this.report);
        }
      } catch (err) {
        this.error = err.response?.data?.mesAr || 'حدث خطأ أثناء تحميل التقرير';
        console.error('Error fetching report:', err);
      } finally {
        this.loading = false;
      }
    },
    formatDate(date) {
      if (!date) return '';
      return new Date(date).toLocaleDateString('ar-SA');
    },
    async downloadPDF() {
      try {
        // Debug logging
        console.log('PDF Generation Debug:');
        console.log('- Selected Organization ID:', this.selectedOrganizationId);
        console.log('- App Logo URL:', this.appLogo);
        if (this.selectedOrganization) {
          console.log('- Org Logo URL:', this.selectedOrganization.logoUrl);
          console.log('- Org Logo Base64:', this.selectedOrganization.logoBase64 ? 'Present' : 'Missing');
        }
        
        // Show PDF header
        const pdfHeader = document.querySelector('.pdf-header');
        if (pdfHeader) {
          pdfHeader.style.display = 'block';
        }

        // Wait a moment for logos to render
        await new Promise(resolve => setTimeout(resolve, 500));

        const element = document.querySelector('.summary-report');
        
        // Generate safe filename
        const sessionName = this.selectedSessionName ? this.selectedSessionName.replace(/[^\u0600-\u06FF\w\s]/g, '') : 'جلسة';
        const orgName = this.selectedOrganization ? this.selectedOrganization.name.replace(/[^\u0600-\u06FF\w\s]/g, '') : '';
        const baseFilename = `تقرير_الجلسات_${sessionName}_${this.selectedDate}${orgName ? '_' + orgName : ''}`;
        const safeFilename = baseFilename.replace(/[^\u0600-\u06FF\w\s-]/g, '').trim() + '.pdf';

        // Ensure element has proper dimensions for full width content capture
        element.style.height = 'auto';
        element.style.minHeight = 'auto';
        element.style.overflow = 'visible';
        element.style.width = '210mm'; // A4 width
        element.style.maxWidth = '210mm';
        element.style.padding = '20px';
        element.style.boxSizing = 'border-box';
        
        // Wait for any dynamic content to render
        await new Promise(resolve => setTimeout(resolve, 300));
        
        const opt = {
          margin: [0.2, 0.2, 0.2, 0.2],
          filename: safeFilename,
          image: { 
            type: 'jpeg', 
            quality: 1.0,
            crossOrigin: 'anonymous'
          },
          html2canvas: { 
            scale: 2,
            useCORS: true,
            allowTaint: true,
            letterRendering: true,
            logging: false,
            width: 794, // A4 width in pixels (210mm * 3.78)
            height: element.scrollHeight,
            scrollX: 0,
            scrollY: 0,
            backgroundColor: '#ffffff',
            windowWidth: 794,
            windowHeight: element.scrollHeight,
            onclone: function(clonedDoc) {
              const clonedElement = clonedDoc.querySelector('.summary-report');
              if (clonedElement) {
                clonedElement.style.height = 'auto';
                clonedElement.style.minHeight = 'auto';
                clonedElement.style.overflow = 'visible';
                clonedElement.style.transform = 'none';
                clonedElement.style.width = '100%';
                clonedElement.style.maxWidth = '100%';
                clonedElement.style.padding = '20px';
                clonedElement.style.boxSizing = 'border-box';
              }
            }
          },
          jsPDF: { 
            unit: 'mm', 
            format: 'a4', 
            orientation: 'portrait',
            compress: true
          },
          pagebreak: { 
            mode: ['avoid-all', 'css', 'legacy'],
            before: '.page-break-before',
            after: '.page-break-after',
            avoid: '.no-break'
          }
        };

        element.classList.add('pdf-export');
        
        // Generate PDF with blob approach for better compatibility
        const pdfBlob = await html2pdf().set(opt).from(element).outputPdf('blob');
        
        // Create download link
        const url = window.URL.createObjectURL(pdfBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = safeFilename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        element.classList.remove('pdf-export');
        
        // Restore original element styles
        element.style.width = '';
        element.style.maxWidth = '';
        element.style.padding = '';
        element.style.boxSizing = '';
        
        // Hide PDF header again
        if (pdfHeader) {
          pdfHeader.style.display = 'none';
        }
        
        console.log('PDF generated successfully');
      } catch (error) {
        console.error('Error generating PDF:', error);
        this.error = 'حدث خطأ أثناء تحميل الملف';
        
        // Clean up in case of error
        const element = document.querySelector('.summary-report');
        const pdfHeader = document.querySelector('.pdf-header');
        if (element) element.classList.remove('pdf-export');
        if (pdfHeader) pdfHeader.style.display = 'none';
      }
    },
    onOrgLogoError(event) {
      console.error('Organization logo failed to load:', event.target.src);
      // Hide the failed image
      event.target.style.display = 'none';
    }
  },
  created() {
    this.fetchSessions();
    this.fetchOrganizations();
  }
};
</script>

<style scoped>
.summary-report {
  direction: rtl;
  text-align: right;
}

.summary-box {
  position: relative;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.summary-box::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(45deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 100%);
  pointer-events: none;
}

.summary-box:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 20px rgba(201, 160, 108, 0.2);
}

.apologies-section {
  transition: all 0.3s ease;
}

.apologies-section:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 16px rgba(201, 160, 108, 0.15);
}

h1, h2, h3, h4 {
  font-family: 'Tajawal', sans-serif;
}

.text-customHafadhColor {
  color: #c9a06c;
}

/* PDF Header Styles */
.pdf-header {
  margin-bottom: 40px;
  direction: rtl;
  text-align: center;
  padding: 20px;
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  border: 2px solid #c9a06c;
  border-radius: 12px;
  box-shadow: 0 8px 16px rgba(201, 160, 108, 0.1);
}

.header-top {
  margin-bottom: 25px;
}

.header-logos {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  height: 100px;
  padding: 0 20px;
}

.logo-left img, .logo-right img {
  max-height: 100px;
  max-width: 150px;
  height: auto;
  width: auto;
  object-fit: contain;
  border-radius: 8px;
  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
}

.logo-placeholder {
  height: 100px;
  width: 100px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #c9a06c 0%, #b8956a 100%);
  border-radius: 8px;
  color: white;
  font-size: 30px;
  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
}

.placeholder-text {
  font-size: 24px;
  font-weight: bold;
  margin-top: 5px;
}

.header-divider {
  height: 3px;
  background: linear-gradient(90deg, #c9a06c 0%, #b8956a 50%, #c9a06c 100%);
  border-radius: 2px;
  margin: 0 20px;
}

.header-content {
  text-align: center;
}

.report-title {
  font-size: 36px;
  font-weight: bold;
  color: #c9a06c;
  margin-bottom: 20px;
  font-family: 'Arial Unicode MS', 'Tahoma', sans-serif;
  text-shadow: 1px 1px 2px rgba(0,0,0,0.1);
}

.report-info {
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: center;
}

.info-row {
  display: flex;
  align-items: center;
  gap: 15px;
  background: rgba(201, 160, 108, 0.1);
  padding: 8px 20px;
  border-radius: 20px;
  border: 1px solid rgba(201, 160, 108, 0.3);
}

.info-label {
  font-size: 18px;
  font-weight: bold;
  color: #c9a06c;
  font-family: 'Arial Unicode MS', 'Tahoma', sans-serif;
  min-width: 60px;
}

.info-value {
  font-size: 18px;
  color: #333;
  font-family: 'Arial Unicode MS', 'Tahoma', sans-serif;
  direction: rtl;
  unicode-bidi: bidi-override;
}

.header-separator {
  height: 4px;
  background: linear-gradient(90deg, transparent 0%, #c9a06c 20%, #b8956a 50%, #c9a06c 80%, transparent 100%);
  margin-top: 20px;
  border-radius: 2px;
}



/* PDF Export Styles */
.pdf-export {
  direction: rtl;
  padding: 20px !important;
  background: white;
  font-family: 'Arial Unicode MS', 'Tahoma', sans-serif;
  width: 100% !important;
  max-width: 100% !important;
  min-width: 100% !important;
  box-sizing: border-box !important;
}

.pdf-export * {
  font-family: 'Arial Unicode MS', 'Tahoma', sans-serif !important;
  direction: rtl !important;
  unicode-bidi: bidi-override !important;
}

.pdf-export .pdf-header {
  display: block !important;
  page-break-after: avoid;
  margin-bottom: 30px;
  width: 100% !important;
  max-width: 100% !important;
}

.pdf-export .grid {
  width: 100% !important;
  max-width: 100% !important;
  display: grid !important;
  grid-template-columns: repeat(4, 1fr) !important;
  gap: 20px !important;
}

.pdf-export .header-logos img {
  max-height: 80px !important;
  max-width: 120px !important;
  height: auto !important;
  width: auto !important;
}

.pdf-export .logo-placeholder {
  height: 80px !important;
  width: 80px !important;
  font-size: 30px !important;
}

.pdf-export .info-value {
  font-size: 20px !important;
  font-weight: bold !important;
}

.pdf-export .summary-box {
  break-inside: avoid;
  page-break-inside: avoid;
  margin-bottom: 25px;
  border: 3px solid #c9a06c !important;
  background: linear-gradient(135deg, #c9a06c 0%, #b8956a 50%, #a68b5f 100%) !important;
  box-shadow: 0 6px 12px rgba(0,0,0,0.15) !important;
  border-radius: 12px !important;
  padding: 20px !important;
  position: relative;
  overflow: hidden;
  width: 100% !important;
  max-width: 100% !important;
  min-height: 120px !important;
  display: flex !important;
  flex-direction: column !important;
  justify-content: center !important;
  align-items: center !important;
  text-align: center !important;
}

.pdf-export .summary-box::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(45deg, rgba(255,255,255,0.1) 0%, transparent 50%, rgba(255,255,255,0.05) 100%);
  pointer-events: none;
}

.pdf-export .summary-box h3 {
  font-size: 24px !important;
  color: white !important;
  font-weight: bold !important;
  text-shadow: 1px 1px 2px rgba(0,0,0,0.3) !important;
  margin-bottom: 10px !important;
}

.pdf-export .summary-box p {
  font-size: 40px !important;
  color: white !important;
  font-weight: bold !important;
  text-shadow: 2px 2px 4px rgba(0,0,0,0.3) !important;
}

.pdf-export .apologies-section {
  border: 3px solid #c9a06c !important;
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%) !important;
  margin-top: 30px;
  margin-bottom: 30px !important;
  page-break-inside: avoid;
  border-radius: 12px !important;
  padding: 25px !important;
  box-shadow: 0 6px 12px rgba(201, 160, 108, 0.2) !important;
  width: 100% !important;
  max-width: 100% !important;
  box-sizing: border-box !important;
  min-height: 100px !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
}

.pdf-export .apologies-section span {
  font-size: 22px !important;
  font-weight: bold !important;
}

.pdf-export .apologies-section span:last-child {
  font-size: 36px !important;
  color: #c9a06c !important;
  text-shadow: 1px 1px 2px rgba(0,0,0,0.1) !important;
}

.pdf-export .student-details-section {
  border: 3px solid #c9a06c !important;
  background: white !important;
  margin-top: 30px !important;
  page-break-inside: avoid;
  border-radius: 12px !important;
  padding: 25px !important;
  box-shadow: 0 6px 12px rgba(201, 160, 108, 0.2) !important;
}

.pdf-export .student-details-section h3 {
  font-size: 28px !important;
  color: #c9a06c !important;
  font-weight: bold !important;
  text-shadow: 1px 1px 2px rgba(0,0,0,0.1) !important;
  margin-bottom: 20px !important;
}

.pdf-export .student-details-section table {
  border: 2px solid #c9a06c !important;
}

.pdf-export .student-details-section th {
  background: linear-gradient(135deg, #c9a06c 0%, #b8956a 100%) !important;
  font-size: 18px !important;
  padding: 15px !important;
  border: 1px solid white !important;
}

.pdf-export .student-details-section td {
  font-size: 16px !important;
  padding: 12px !important;
  border: 1px solid #c9a06c !important;
}

.pdf-export .student-details-section .bg-blue-100 {
  background-color: #dbeafe !important;
  color: #1e40af !important;
}

.pdf-export .student-details-section .bg-green-100 {
  background-color: #dcfce7 !important;
  color: #166534 !important;
}

.pdf-export .student-details-section .bg-customHafadhColor {
  background-color: #c9a06c !important;
  color: white !important;
}

/* Add Font Awesome if not already included in your project */
@import url('https://cdnjs.cloudflare.com/ajax/libs/font-awesome/5.15.4/css/all.min.css');
</style>